/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import requestCountry from 'js/lib/requestCountry';

import { Button, Typography2 } from '@coursera/cds-core';

import ContentfulError, { ERROR_MESSAGES } from 'bundles/coursera-plus-landing-page/components/ContentfulError';
import SimpleRichTextContentful from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import type { ConnectedPiece } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulLine';
import Pretranslated from 'bundles/coursera-plus-landing-page/components/ctaContentful/Pretranslated';
import EnrollButton from 'bundles/coursera-plus-landing-page/components/enrollButtonContentful/EnrollButton';
import {
  CTA_LOCATIONS,
  PRESENTATIONS,
  PRODUCT_CHOICES,
  actionComponent,
  renewSubscriptionLabel,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { useGetCourseraPlusAnnualWithFreeTrialProduct } from 'bundles/coursera-plus-landing-page/utils/courseraPlusAnnualWithFreeTrial/useGetCourseraPlusAnnualWithFreeTrialProduct';
import { useIsRedesignPromoPageEnabled } from 'bundles/coursera-plus-landing-page/utils/useIsRedesignPromoPageEnabled';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import {
  isMonthlyByVariantId as isMonthly,
  hasFreeTrialByVariantId as productHasFreeTrial,
} from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';

import _t from 'i18n!nls/coursera-plus-landing-page';

export type PropsForContentfulPiece = {
  piece: ConnectedPiece;
  ctaLocation: string;
  key?: number;
  singleBigButton?: boolean;
  rootClassName?: string;
};

export type ErrorMessageFunction = {
  showError: (message: string, pretranslated?: string) => JSX.Element;
};

const styles = {
  globalPromoText: css`
    font-size: 22px;
    line-height: 1.6;
    font-weight: bold;
  `,
  globalPromoTermsLink: css`
    padding: 0 0 2px 0;
    margin-top: 0 !important;
  `,
};

const getCustomDisclaimerCopy = (slug?: string | null) => {
  const learnerCountry = requestCountry.get();
  const slugListToShowProminentExpirationDate = [
    'global-new-year-2025-50',
    'global-new-year-2025-199',
    'new-year-in',
    'em-new-year',
    'new-year-de',
    'new-year-latam',
    'new-year-fr',
  ];

  const handleClickTerms = () => {
    if (typeof window === 'undefined') {
      return;
    }
    const element = document && document.getElementById('disclaimer-on-footer');
    const headerOffset = 80; // To account for the space covered by the page header
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  if (slug === 'em-promotion') {
    return {
      topDisclaimerCopy: (
        <FormattedMessage
          message={_t('Limited Time Offer, applicable to certain regions only. See our {offerTermsLink}.')}
          offerTermsLink={
            <Button variant="ghost" size="medium" onClick={handleClickTerms} style={{ padding: 0 }}>
              {_t('Offer Terms')}
            </Button>
          }
        />
      ),
      termsTitle: _t('Offer Terms'),
    };
  } else if (slugListToShowProminentExpirationDate.includes(slug as string)) {
    return {
      topDisclaimerCopy: (
        <Typography2 component="span" variant="subtitleLarge" css={styles.globalPromoText}>
          <FormattedMessage
            message={_t('Offer ends January 27, 2025. See {offerTermsLink}.')}
            offerTermsLink={
              <Button
                variant="ghost"
                size="medium"
                onClick={handleClickTerms}
                css={[styles.globalPromoText, styles.globalPromoTermsLink]}
              >
                {_t('Offer Terms')}
              </Button>
            }
          />
        </Typography2>
      ),
      termsTitle: _t('Offer Terms'),
    };
  } else if (['global-160-2024', 'global-40-2024'].includes(slug as string)) {
    return {
      topDisclaimerCopy: (
        <Typography2 component="span" variant="subtitleLarge" css={styles.globalPromoText}>
          <FormattedMessage
            message={_t('Offer ends December 2, 2024. See {offerTermsLink}.')}
            offerTermsLink={
              <Button
                variant="ghost"
                size="medium"
                onClick={handleClickTerms}
                css={[styles.globalPromoText, styles.globalPromoTermsLink]}
              >
                {_t('Offer Terms')}
              </Button>
            }
          />
        </Typography2>
      ),
      termsTitle: _t('Offer Terms'),
    };
  } else {
    if (learnerCountry === 'IN') {
      return {
        topDisclaimerCopy: (
          <FormattedMessage
            message={_t('*Limited Time Offer, see our {offerTermsLink}.')}
            offerTermsLink={
              <Button variant="ghost" size="medium" onClick={handleClickTerms} style={{ padding: 0 }}>
                {_t('Offer Terms')}
              </Button>
            }
          />
        ),
        termsTitle: _t('Offer Terms'),
      };
    } else if (learnerCountry === 'BR') {
      return {
        topDisclaimerCopy: (
          <FormattedMessage
            message={_t('*Oferta por Tempo Limitado. Acesse os {offerTermsLink}.')}
            offerTermsLink={
              <Button variant="ghost" size="medium" onClick={handleClickTerms} style={{ padding: 0 }}>
                {_t('Termos da Nossa Oferta')}
              </Button>
            }
          />
        ),
        termsTitle: _t('Termos da Nossa Oferta'),
      };
    } else if (['MX', 'CO'].includes(learnerCountry)) {
      return {
        topDisclaimerCopy: (
          <FormattedMessage
            message={_t('*Oferta por tiempo limitado, consulta nuestros {offerTermsLink}.')}
            offerTermsLink={
              <Button variant="ghost" size="medium" onClick={handleClickTerms} style={{ padding: 0 }}>
                {_t('términos de la oferta')}
              </Button>
            }
          />
        ),
        termsTitle: _t('Términos de la oferta'),
      };
    } else {
      return {
        topDisclaimerCopy: (
          <FormattedMessage
            message={_t('*Limited Time Offer, see our {offerTermsLink}.')}
            offerTermsLink={
              <Button variant="ghost" size="medium" onClick={handleClickTerms} style={{ padding: 0 }}>
                {_t('Offer Terms')}
              </Button>
            }
          />
        ),
        termsTitle: _t('Offer Terms'),
      };
    }
  }
};

const CTAContentfulPiece = (props: PropsForContentfulPiece) => {
  const { ctaLocation, singleBigButton, rootClassName } = props;
  const { product, presentation, size } = props.piece;
  const landingPageExtended = useLandingPageExtended();
  const showError = (errorMessage: string, pretranslated?: string, entryTitle?: string): JSX.Element => (
    <ContentfulError
      errorMessage={errorMessage}
      pretranslated={pretranslated}
      ctaLocation={ctaLocation}
      ctaPieceTitle={entryTitle}
    />
  );
  const {
    primaryProductItemId,
    secondaryProductItemId,
    userHasCourseraPlusFreeTrial,
    userHasCourseraLiteFreeTrial,
    isCourseraLiteLandingPageEnabled,
    isCourseraPlusOwner,
    landingPage,
    courseraPlusAnnualWithFreeTrialEpicVariant,
  } = landingPageExtended;
  const isRedesignPromoPageEnabled = useIsRedesignPromoPageEnabled();
  const { courseraPlusAnnualProductAndPrice, annualPriceDisplay } = useGetCourseraPlusAnnualWithFreeTrialProduct();
  const { labelForPrimaryCheckout, promotionCode } = landingPageExtended.landingPage;
  const courseraPlusAnnualWithFreeTrialProductItemId = courseraPlusAnnualProductAndPrice?.productItemId;
  const isVariantOneOfCourseraPlusAnnualWithFreeTrialEpicTest = courseraPlusAnnualWithFreeTrialEpicVariant === '1';

  let productItemId: SubscriptionTiersProductVariant = primaryProductItemId;
  let isPrimary = true;
  if (product === PRODUCT_CHOICES.secondary) {
    if (!secondaryProductItemId) {
      return showError(ERROR_MESSAGES.missingSecondary);
    }
    productItemId =
      isVariantOneOfCourseraPlusAnnualWithFreeTrialEpicTest && courseraPlusAnnualWithFreeTrialProductItemId
        ? (courseraPlusAnnualWithFreeTrialProductItemId as SubscriptionTiersProductVariant)
        : secondaryProductItemId;
    isPrimary = false;
  }
  const isFooter = ctaLocation === CTA_LOCATIONS.footer;
  const isHero = ctaLocation === CTA_LOCATIONS.hero;

  const connectedMessage: Array<JSX.Element> = [];
  let key = 0;
  for (const messagePiece of props.piece.messages) {
    if ((isFooter && messagePiece.hideInLocation === 'Footer') || (isHero && messagePiece.hideInLocation === 'Hero')) {
      return null;
    }

    // This is a quick fix for some regional promos only. See context here: https://coursera.slack.com/archives/C06202RPRS7/p1724945446540989?thread_ts=1724754638.365669&cid=C06202RPRS7
    const displayMessageOnFooterOnly = messagePiece.entryTitle?.includes('[display-on-footer-only]');

    if (isHero && displayMessageOnFooterOnly) {
      const { topDisclaimerCopy } = getCustomDisclaimerCopy(landingPage?.slug);
      return topDisclaimerCopy;
    }
    key += 1;
    const { pretranslated, freeform, freeformRich, entryTitle, displayAsBold } = messagePiece;
    const filledContentFieldCount = [freeformRich, freeform, pretranslated].filter((val) => !!val).length;
    // If no other text content is specified and the piece is related to the Primary Product
    // use Label For Primary Checkout from the Coursera Plus Landing Page entry
    let useLabelForPrimary = false;
    if (filledContentFieldCount === 0) {
      if (isPrimary && labelForPrimaryCheckout && presentation !== PRESENTATIONS.plain) {
        useLabelForPrimary = true;
      } else {
        return showError(ERROR_MESSAGES.missingMessage, undefined, entryTitle ?? undefined);
      }
    }
    // can't have more than one content fields filled
    if (filledContentFieldCount > 1) {
      return showError(ERROR_MESSAGES.multipleMessages, undefined, entryTitle ?? undefined);
    }

    // create message JSX or return error
    let message: JSX.Element;
    if (useLabelForPrimary) {
      message = <span key={key}>{labelForPrimaryCheckout}</span>;
    } else if (freeformRich) {
      if (isFooter && displayMessageOnFooterOnly) {
        const { termsTitle } = getCustomDisclaimerCopy();
        message = (
          <div id="disclaimer-on-footer">
            <Typography2 variant="titleMedium" component="h3" color="invertBody" style={{ marginBottom: '20px' }}>
              {termsTitle}
            </Typography2>
            <div style={{ fontSize: '18px' }}>
              <SimpleRichTextContentful richText={freeformRich} key={key} />
            </div>
          </div>
        );
      } else {
        message = <SimpleRichTextContentful richText={freeformRich} key={key} />;
      }
    } else if (freeform) {
      message = <span key={key}>{freeform}</span>;
    } else if (pretranslated) {
      message = (
        <Pretranslated
          key={key}
          messageKey={pretranslated}
          productItemId={productItemId}
          isPrimary={product === PRODUCT_CHOICES.primary}
          showError={(errorMessage, wrongPretranslated) =>
            showError(errorMessage, wrongPretranslated, entryTitle ?? undefined)
          }
          // Ideally, for an A/B test of one landing page variant vs another,
          // a flag like shouldHideSocialProofCopy should be set on the
          // Contentful model for each individual copy/component.
          shouldHideSocialProofCopy={isRedesignPromoPageEnabled}
        />
      );
    } else {
      // shouldn't happen
      return null;
    }

    if (displayAsBold) {
      message = <b key={key}>{message}</b>;
    }
    // todo: finalize styling, introduce constant for sizes
    if (size === 'Big') {
      message = (
        <span key={key} style={{ fontSize: '40px', fontWeight: 'bold', lineHeight: 1.4 }}>
          {message}
        </span>
      );
    }
    if (size === 'Medium') {
      message = (
        <span key={key} style={{ fontSize: '22px', lineHeight: 1.6 }}>
          {message}
        </span>
      );
    }

    connectedMessage.push(message);
    connectedMessage.push(<span key={'--' + key}> </span>);
  }
  const finalMessage: JSX.Element = <React.Fragment>{connectedMessage}</React.Fragment>;
  if (presentation === PRESENTATIONS.plain || !presentation) {
    return (
      <Typography2
        variant="bodyPrimary"
        component="span"
        color={ctaLocation === CTA_LOCATIONS.footer ? 'invertBody' : 'body'}
      >
        {finalMessage}
      </Typography2>
    );
  }
  const hasFreeTrial = isCourseraLiteLandingPageEnabled ? userHasCourseraLiteFreeTrial : userHasCourseraPlusFreeTrial;
  const showRenewalMessage =
    isPrimary && productHasFreeTrial(primaryProductItemId) && !hasFreeTrial && !isCourseraPlusOwner;

  // At this point everything that is not plain text leads you to checkout
  const ActionComponent = actionComponent(presentation, !!singleBigButton, ctaLocation);
  const trackingName: string = ctaLocation + '_' + (isMonthly(productItemId) ? 'monthly' : 'annual');
  const promoCode = isPrimary ? promotionCode : undefined;

  const getLabel = () => {
    if (
      !isPrimary &&
      isVariantOneOfCourseraPlusAnnualWithFreeTrialEpicTest &&
      courseraPlusAnnualWithFreeTrialProductItemId &&
      annualPriceDisplay
    ) {
      if (hasFreeTrial) {
        return <FormattedMessage message={_t('or {price}/year with 7-day free trial')} price={annualPriceDisplay} />;
      } else {
        return <FormattedMessage message={_t('or {price}/year, cancel anytime')} price={annualPriceDisplay} />;
      }
    } else if (showRenewalMessage) {
      return renewSubscriptionLabel();
    } else {
      return finalMessage;
    }
  };

  return (
    <EnrollButton
      renderAsMonthlySubscriptionLink={true}
      trackingName={trackingName}
      productItemId={productItemId}
      PresentationComponent={ActionComponent}
      label={getLabel()}
      rootClassName={rootClassName}
      promoCode={promoCode ? promoCode.toString() : undefined}
      shouldCheckActionParams={props.piece.isFirstCheckoutInHero}
      userHasCourseraLiteFreeTrial={isCourseraLiteLandingPageEnabled && userHasCourseraLiteFreeTrial}
      userHasCourseraPlusFreeTrial={userHasCourseraPlusFreeTrial}
    />
  );
};

export default CTAContentfulPiece;
